/**
 * Utilitários para manipulação de datas com timezone BRT
 * 
 * IMPORTANTE: Sempre use estas funções ao invés de new Date().toISOString()
 * para evitar bugs de timezone após 21:00 BRT (quando UTC já mudou de dia)
 */

// Formata uma Date para YYYY-MM-DD no timezone America/Sao_Paulo
export function formatDateBRT(date: Date): string {
  const fmt = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Sao_Paulo',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  return fmt.format(date); // en-CA => YYYY-MM-DD
}

/**
 * Retorna a data atual no timezone BRT (America/Sao_Paulo)
 * @returns Data no formato YYYY-MM-DD em BRT
 */
export function getTodayBRT(): string {
  return formatDateBRT(new Date());
}

/**
 * Converte uma data UTC para BRT
 * @param utcDate Data em UTC (string ISO ou Date)
 * @returns Data no formato YYYY-MM-DD em BRT
 */
export function convertUTCtoBRT(utcDate: string | Date): string {
  const date = typeof utcDate === 'string' ? new Date(utcDate) : utcDate;
  return formatDateBRT(date);
}

/**
 * Retorna o timestamp atual em BRT
 * @returns Date object ajustado para BRT
 */
export function getNowBRT(): Date {
  // Usa a data atual, mas o consumo deve sempre usar formatDateBRT para gerar YYYY-MM-DD
  return new Date();
}

/**
 * Verifica se duas datas são do mesmo dia em BRT
 * @param date1 Primeira data (UTC)
 * @param date2 Segunda data (UTC)
 * @returns true se são do mesmo dia em BRT
 */
export function isSameDayBRT(date1: string | Date, date2: string | Date): boolean {
  return convertUTCtoBRT(date1) === convertUTCtoBRT(date2);
}

/**
 * Soma dias a um YYYY-MM-DD (base interpretada em BRT) e retorna YYYY-MM-DD em BRT
 */
export function addDaysBRTFrom(baseYmd: string, days: number): string {
  const [y, m, d] = baseYmd.split('-').map(Number);
  // Usa meio-dia UTC para evitar bordas de fuso
  const base = new Date(Date.UTC(y, (m || 1) - 1, d || 1, 12, 0, 0));
  base.setUTCDate(base.getUTCDate() + days);
  return formatDateBRT(base);
}

/**
 * Retorna o próximo Monday (segunda-feira) a partir de um YYYY-MM-DD (em BRT)
 * Caso seja domingo (0), retorna +1; caso contrário, 8 - dia.
 */
export function nextMondayBRT(fromYmd?: string): string {
  const baseYmd = fromYmd ?? getTodayBRT();
  const [y, m, d] = baseYmd.split('-').map(Number);
  // Constrói uma data em BRT (fixa -03:00) no meio do dia para evitar troca de dia
  const base = new Date(`${String(y).padStart(4, '0')}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}T12:00:00-03:00`);
  const day = base.getUTCDay(); // 0..6
  const daysUntilMonday = day === 0 ? 1 : (8 - day);
  const next = new Date(base);
  next.setUTCDate(next.getUTCDate() + daysUntilMonday);
  return formatDateBRT(next);
}
